<template>
  <b-card no-body class="border mb-2">
    <b-card-header class="pb-1">
      <div>
        <b-card-title class="mb-25">Fixed values</b-card-title>
        <b-card-text class="text-muted small mb-0">
          Values every record this client writes carries, whatever its request said. Use
          this when a classification belongs to the integration rather than to the call —
          "every profile this partner creates is a mission member". A fixed value
          <strong>overrides</strong> anything the client sends, on creates and on updates
          alike, so the tag cannot be edited off a record afterwards.
        </b-card-text>
      </div>
    </b-card-header>

    <b-card-body>
      <b-alert
        v-if="!writableResources.length"
        variant="secondary"
        show
        :class="rows.length ? 'mb-1' : 'mb-0'"
      >
        <div class="alert-body">
          No resource on this server accepts writes, so there is nothing to fix a value on.
        </div>
      </b-alert>

      <template v-if="rows.length || writableResources.length">
        <!--
          One panel per fixed value rather than table rows: a table needs a horizontal
          scroller to stay responsive, and that scroller clips the select menus that open
          inside it.
        -->
        <div
          v-for="(row, index) in rows"
          :key="`fixed-${index}`"
          class="fixed-value border rounded p-1 mb-1"
        >
          <b-row>
            <b-col cols="12" md="4">
              <b-form-group label="Resource" class="mb-1 mb-md-0">
                <v-select
                  :value="row.resource"
                  :options="resourceOptions"
                  :reduce="option => option.value"
                  :clearable="false"
                  :disabled="disabled"
                  @input="onResourceChange(index, $event)"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="4">
              <b-form-group class="mb-1 mb-md-0">
                <template #label>
                  Field
                  <span v-if="fieldType(row)" class="text-muted font-weight-normal">
                    · {{ typeLabel(fieldType(row)) }}
                  </span>
                </template>

                <v-select
                  :value="row.field"
                  :options="fieldOptions(row.resource)"
                  :reduce="option => option.value"
                  :clearable="false"
                  :disabled="disabled || !row.resource"
                  placeholder="Pick a field"
                  @input="onFieldChange(index, $event)"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="4">
              <b-form-group label="Always set to" class="mb-0">
                <!-- Boolean -->
                <b-form-radio-group
                  v-if="fieldType(row) === 'Boolean'"
                  :checked="row.value === true"
                  buttons
                  button-variant="outline-primary"
                  :disabled="disabled"
                  @change="setValue(index, $event)"
                >
                  <b-form-radio :value="true">True</b-form-radio>
                  <b-form-radio :value="false">False</b-form-radio>
                </b-form-radio-group>

                <!-- Number -->
                <b-form-input
                  v-else-if="fieldType(row) === 'Number'"
                  :value="row.value"
                  type="number"
                  :disabled="disabled"
                  @input="setValue(index, $event === '' ? null : Number($event))"
                />

                <!-- Json -->
                <template v-else-if="fieldType(row) === 'Mixed'">
                  <b-form-textarea
                    :value="jsonText(index, row)"
                    rows="3"
                    :placeholder="jsonPlaceholder"
                    :state="jsonErrors[index] ? false : null"
                    :disabled="disabled"
                    @input="setJsonValue(index, $event)"
                  />
                  <small v-if="jsonErrors[index]" class="text-danger">
                    {{ jsonErrors[index] }}
                  </small>
                  <small v-else class="text-muted">JSON — the whole field is replaced</small>
                </template>

                <!-- Text, ids, everything else -->
                <b-form-input
                  v-else
                  :value="row.value"
                  :disabled="disabled || !row.field"
                  :placeholder="valuePlaceholder(row)"
                  @input="setValue(index, $event)"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex align-items-start justify-content-between mt-1">
            <div class="small pr-1">
              <span v-if="summary(row)" class="text-muted">
                <feather-icon icon="CornerDownRightIcon" size="14" class="mr-25" />
                {{ summary(row) }}
              </span>
              <span v-else class="text-muted">Pick a field to fix.</span>

              <div v-if="row.resource && !clientWrites(row.resource)" class="text-warning mt-25">
                <feather-icon icon="AlertCircleIcon" size="14" class="mr-25" />
                No Create or Update permission here yet, so this takes effect once one is
                granted.
              </div>
            </div>

            <b-button
              variant="flat-danger"
              size="sm"
              class="btn-icon flex-shrink-0"
              :disabled="disabled"
              @click="removeRow(index)"
            >
              <feather-icon icon="Trash2Icon" />
            </b-button>
          </div>
        </div>

        <div v-if="!rows.length" class="text-muted small mb-1">
          Nothing is fixed — this client's records carry exactly what it sends.
        </div>

        <b-button
          v-if="writableResources.length"
          variant="outline-primary"
          size="sm"
          :disabled="disabled"
          @click="addRow"
        >
          <feather-icon icon="PlusIcon" class="mr-50" />
          Fix a value
        </b-button>

        <b-alert v-if="scopeConflicts.length" variant="warning" show class="mt-1 mb-0">
          <div class="alert-body small">
            <div v-for="(conflict, index) in scopeConflicts" :key="index">
              <feather-icon icon="AlertTriangleIcon" class="mr-50" />
              <code>{{ conflict.field }}</code> is fixed to a value outside this client's
              own <strong>{{ conflict.dimension }}</strong> scope, so every write would be
              refused.
            </div>
          </div>
        </b-alert>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import vSelect from 'vue-select'
import {
  BRow,
  BCol,
  BCard,
  BAlert,
  BButton,
  BCardBody,
  BCardText,
  BCardTitle,
  BCardHeader,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormTextarea,
  BFormRadioGroup,
} from 'bootstrap-vue'

// A fixed value only ever lands on a create or an update.
const WRITE_ACTIONS = ['CanAddNew', 'CanUpdate']

// The mongoose type names the registry reports, said the way an admin reads them.
const TYPE_LABELS = {
  Boolean: 'true or false',
  Number: 'number',
  String: 'text',
  ObjectId: 'record id',
  Mixed: 'json',
  Date: 'date',
}

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BAlert,
    BButton,
    BCardBody,
    BCardText,
    BCardTitle,
    BCardHeader,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormTextarea,
    BFormRadioGroup,
    vSelect,
  },
  props: {
    // [{ resource, field, value }]
    value: {
      type: Array,
      default: () => [],
    },
    // registry entries: [{ resource, supported_actions, fixable_fields, dimensions }]
    resources: {
      type: Array,
      default: () => [],
    },
    // [{ resource, actions: [] }] — what this client may actually do
    permissions: {
      type: Array,
      default: () => [],
    },
    // [{ resource, dimension_key, mode, values }] — used only to warn, never to block
    scopeRules: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Kept beside the rows rather than in them: a half-typed object is not a value
      // worth emitting, but it is text the admin must keep seeing while they finish it.
      jsonDrafts: {},
      jsonErrors: {},
      jsonPlaceholder: '{ "source": "partner-x" }',
    }
  },
  computed: {
    rows() {
      return this.value || []
    },
    /**
     * Every resource that *can* be written, not every resource this client may write
     * today. Fixing a value and granting the permission are two separate decisions, and
     * an admin should be able to make them in either order — gating the list on the
     * permission hides `member_profiles` from the one form whose whole purpose is
     * tagging member profiles. A row whose permission is still missing says so instead.
     */
    writableResources() {
      return this.resources.filter(resource => {
        const supported = resource.supported_actions || []
        return supported.some(action => WRITE_ACTIONS.includes(action))
      })
    },
    resourceOptions() {
      return this.writableResources.map(resource => ({
        value: resource.resource,
        label: this.humanize(resource.resource),
      }))
    },
    /**
     * A value pinned outside the client's own scope makes every write fail — the scope
     * check runs on the stamped payload. Worth saying at configuration time; not worth
     * refusing to save over, since the admin may be about to widen the scope.
     */
    scopeConflicts() {
      const conflicts = []

      this.rows.forEach(row => {
        const resource = this.resources.find(entry => entry.resource === row.resource)
        if (!resource || !row.field) return

        const dimension = (resource.dimensions || []).find(entry => entry.field === row.field)
        if (!dimension) return

        const rule = this.scopeRules.find(
          entry => entry.resource === row.resource && entry.dimension_key === dimension.key,
        )
        if (!rule || rule.mode === 'all') return

        const allowed = (rule.values || []).map(String)
        if (allowed.includes(String(row.value))) return

        conflicts.push({ field: row.field, dimension: this.humanize(dimension.key) })
      })

      return conflicts
    },
  },
  methods: {
    humanize(value) {
      return String(value)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    typeLabel(type) {
      return TYPE_LABELS[type] || String(type).toLowerCase()
    },
    clientWrites(resource) {
      const grant = this.permissions.find(entry => entry.resource === resource)
      if (!grant) return false

      return (grant.actions || []).some(action => WRITE_ACTIONS.includes(action))
    },
    registryFor(resource) {
      return this.resources.find(entry => entry.resource === resource)
    },
    fieldOptions(resource) {
      const registry = this.registryFor(resource)
      if (!registry) return []

      return (registry.fixable_fields || []).map(entry => ({
        value: entry.field,
        label: entry.field,
      }))
    },
    fieldType(row) {
      const registry = this.registryFor(row.resource)
      if (!registry) return ''

      const match = (registry.fixable_fields || []).find(entry => entry.field === row.field)
      return match ? match.type : ''
    },
    valuePlaceholder(row) {
      if (!row.field) return 'Pick a field first'
      return this.fieldType(row) === 'ObjectId' ? 'Record id' : 'Value'
    },
    /** The row said back as a sentence, so an admin can check it without re-reading the controls. */
    summary(row) {
      if (!row.field) return ''

      const type = this.fieldType(row)
      const blank = row.value === '' || row.value === null || row.value === undefined

      let value
      if (type === 'Boolean') value = row.value === true ? 'true' : 'false'
      else if (type === 'Mixed') value = blank ? 'nothing yet' : JSON.stringify(row.value)
      else if (blank) value = 'nothing yet'
      else value = `"${row.value}"`

      return `On ${this.humanize(row.resource)}, ${row.field} is always ${value}.`
    },
    emit(rows) {
      this.$emit('input', rows)
    },
    addRow() {
      const resource = this.resourceOptions.length ? this.resourceOptions[0].value : ''
      this.emit([...this.rows, { resource, field: '', value: '' }])
    },
    removeRow(index) {
      // Drafts are keyed by position, and removing a row shifts every position after
      // it — so they are dropped wholesale rather than left pointing at the wrong row.
      this.jsonDrafts = {}
      this.jsonErrors = {}
      this.emit(this.rows.filter((row, position) => position !== index))
    },
    replaceRow(index, patch) {
      this.emit(this.rows.map((row, position) => (position === index ? { ...row, ...patch } : row)))
    },
    onResourceChange(index, resource) {
      // The field list is per resource, so a field carried across would name something
      // that does not exist on the new one.
      this.replaceRow(index, { resource, field: '', value: '' })
    },
    onFieldChange(index, field) {
      const type = this.fieldType({ ...this.rows[index], field })

      // True is the answer in nearly every case this feature exists for.
      let value = ''
      if (type === 'Boolean') value = true
      if (type === 'Mixed') value = null

      this.$delete(this.jsonDrafts, index)
      this.$delete(this.jsonErrors, index)
      this.replaceRow(index, { field, value })
    },
    setValue(index, value) {
      this.replaceRow(index, { value })
    },
    jsonText(index, row) {
      if (this.jsonDrafts[index] !== undefined) return this.jsonDrafts[index]
      if (row.value === null || row.value === undefined || row.value === '') return ''

      return JSON.stringify(row.value, null, 2)
    },
    setJsonValue(index, text) {
      this.$set(this.jsonDrafts, index, text)

      if (!String(text).trim()) {
        this.$delete(this.jsonErrors, index)
        this.replaceRow(index, { value: null })
        return
      }

      try {
        const parsed = JSON.parse(text)
        this.$delete(this.jsonErrors, index)
        this.replaceRow(index, { value: parsed })
      } catch (error) {
        this.$set(this.jsonErrors, index, 'Not valid JSON yet')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// The open select menu has to escape its panel — no scroll container anywhere on the
// way out, which is what clipped it inside the table this replaced.
.fixed-value {
  background-color: rgba($color: #b8c2cc, $alpha: 0.06);

  .vs__dropdown-menu {
    z-index: 10;
  }
}
</style>
